import {
  MarketsDropdown,
  MarketInfo,
  PositionsTableContainer,
} from '@/features/trade/components';
import TradingView from '@/features/trade/components/tv-chart';
import { OrderFormContainer } from '@/features/trade/order/components';
import CryptoJS from 'crypto-js';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { useTickerLatest } from '@/features/markets/api/get-ticker-latest';
import OpenOrdersTable from '@/features/account/components/open-orders-table';
import TransfersTable from '@/features/account/components/transfers-table';
import { useEffect, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { OrderHistoryFilterType, OrderType, TableTabType } from '@/types/enums';
import { useMarketStore } from '@/store/use-markets-store';
import { bigIntToDecimalStr } from '@/utils/value-format';
import { useStore } from 'zustand';
import LeverageModal from '@/components/header/leverage-modal';
import Activity from '@/features/markets/components/activity';
import {
  AuthParams,
  useAccountStore,
  useCurrentAccount,
} from '@/store/use-account-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { useUnifiedAccount } from '@/hooks';
import useViewport from '@/hooks/use-viewport';
import { cn } from '@/lib/utils';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import JoinDialog from '@/features/referrals/components/join-dialog';
import { updatePrivateApiHeaders } from '@/lib/api-clients/rest-client';
import OrdersHistoryTable from '@/features/account/components/orders-history-table';

export default function TradePage() {
  const [searchParams] = useSearchParams();

  const { setAuthParams, setHasAuth, setAddress, setIsMobileConnected } =
    useAccountStore();

  const defaultTab = searchParams.get('view') as TableTabType;
  const isDefaultTabCorrect =
    !!defaultTab && Object.values(TableTabType).includes(defaultTab);
  const [tableTab, setTableTab] = useState<TableTabType>(
    isDefaultTabCorrect ? defaultTab : TableTabType.POSITIONS,
  );
  const [orderHistoryFilter, setOrderHistoryFilter] =
    useState<OrderHistoryFilterType>(OrderHistoryFilterType.ALL);
  const { symbol } = useParams() as { symbol: string };

  const viewport = useViewport();

  const isMobile = viewport == 'sm';

  const { marketData, marketSpec } = useStore(
    useMarketStore(symbol),
    (state) => state,
  );
  const { positions } = useCurrentAccount();
  const { address } = useUnifiedAccount();

  const openOrders = useOrdersStore((state) => {
    const { orders, newOrderIds } = state.getOrders(address);

    return Array.from(newOrderIds, (id) => orders[id]).filter(
      (order) => order.orderType !== OrderType.MARKET,
    );
  });

  // const { isSuccess: isAccountSuccess } = useFetchAccount({});
  useTickerLatest(); // TEMP for MarketsDropdown

  useEffect(() => {
    document.title = `${bigIntToDecimalStr(
      marketData.markPrice,
      marketSpec.priceDecimals,
    )} | ${symbol} | Vest`;
  }, [marketData.markPrice, marketSpec.priceDecimals]);

  const location = useLocation();

  useEffect(() => {
    if (!isMobile) return;
    const searchParams = new URLSearchParams(location.search);
    const encodedLink = searchParams.get('link');
    if (encodedLink) {
      const bytes = CryptoJS.AES.decrypt(
        encodedLink,
        import.meta.env.VITE_REST_URL,
      );
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      const parsedData: AuthParams = JSON.parse(decryptedData);

      const { address, accountGroup, apiKey } = parsedData;
      setAddress(address);
      setIsMobileConnected(true);
      setAuthParams(address, {
        ...parsedData,
        listenKey: '',
        listenKeyExpiry: 0,
        shouldPersist: true,
        connector: undefined,
      });
      updatePrivateApiHeaders({
        xrestservermm: `restserver${accountGroup}`,
        'X-API-KEY': apiKey,
      });

      setHasAuth(true);
    }
  }, [location, isMobile]);

  useEffect(() => {
    if (!isDefaultTabCorrect) return;
    setTableTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    localStorage.setItem('last-market', symbol);
  }, [symbol]);

  return (
    <div className="flex h-full flex-col">
      <JoinDialog />
      <div className="relative flex h-16 shrink-0 border-b border-border bg-background sm:h-20">
        <div className="flex flex-1 md:grid-cols-[320px_1fr]">
          <MarketsDropdown />
          <MarketInfo />
          {/* {isLong !== undefined && viewport === 'sm' ? (
            <LeverageModal />
          ) : (
            <MarketInfo />
          )} */}
        </div>
        {/* {viewport !== 'sm' && <LeverageModal />} */}
        <LeverageModal />
      </div>
      <div className="relative flex flex-1 flex-col md:flex-row">
        <div className="flex max-h-[228px] min-h-[200px] flex-1 flex-col md:max-h-none md:min-h-0">
          <ResizablePanelGroup direction="vertical">
            <ResizablePanel className="lg:min-h-48">
              <div className="flex h-full">
                <TradingView symbol={symbol || 'BTC-PERP'} />
                {viewport === 'lg' && <Activity />}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle className="hidden lg:flex" />
            {viewport === 'lg' && (
              <ResizablePanel minSize={30.5} defaultSize={30.5}>
                <Tabs
                  value={tableTab}
                  onValueChange={(value) => setTableTab(value as TableTabType)}
                  className="flex h-full w-full flex-col"
                >
                  <div className="flex">
                    <TabsList>
                      <TabsTrigger
                        className="gap-2 border-t-0"
                        value="positions"
                      >
                        POSITIONS
                        <span className="text-vestgrey-100">
                          {Object.keys(positions).length > 0
                            ? `(${Object.keys(positions).length})`
                            : ''}
                        </span>
                      </TabsTrigger>
                      <TabsTrigger
                        className="gap-2 border-t-0"
                        value="open-orders"
                      >
                        OPEN ORDERS
                        <span className="text-vestgrey-100">
                          {openOrders.length > 0
                            ? `(${openOrders.length})`
                            : ''}
                        </span>
                      </TabsTrigger>
                      <TabsTrigger className="border-t-0" value="order-history">
                        ORDER HISTORY
                      </TabsTrigger>
                      <TabsTrigger className="border-t-0" value="transfers">
                        TRANSFERS
                      </TabsTrigger>
                    </TabsList>
                    <div className="flex w-full items-center justify-end gap-4 border-b border-border bg-background font-mono">
                      {tableTab === TableTabType.ORDER_HISTORY && (
                        <Select
                          onValueChange={(value) =>
                            setOrderHistoryFilter(
                              value as OrderHistoryFilterType,
                            )
                          }
                        >
                          <SelectTrigger
                            id="filter"
                            className="h-full max-w-32 rounded-none border-y-0 border-r-0"
                          >
                            <SelectValue placeholder="Filter" />
                          </SelectTrigger>
                          <SelectContent className="font-mono">
                            <SelectItem value={OrderHistoryFilterType.ALL}>
                              All
                            </SelectItem>
                            <SelectItem value={OrderHistoryFilterType.FILLED}>
                              Filled
                            </SelectItem>
                            <SelectItem value={OrderHistoryFilterType.REJECTED}>
                              Rejected
                            </SelectItem>
                            <SelectItem
                              value={OrderHistoryFilterType.CANCELLED}
                            >
                              Cancelled
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                  <TabsContent
                    className="relative min-h-48 flex-1"
                    value="positions"
                  >
                    <div className="absolute inset-0 h-full w-full pb-12">
                      <PositionsTableContainer />
                    </div>
                  </TabsContent>
                  <TabsContent
                    className="relative min-h-48 flex-1"
                    value="open-orders"
                  >
                    <div className="absolute inset-0 h-full w-full pb-12">
                      <OpenOrdersTable />
                    </div>
                  </TabsContent>
                  <TabsContent
                    className="relative min-h-48 flex-1"
                    value="order-history"
                  >
                    <div className="absolute inset-0 h-full w-full pb-12">
                      <OrdersHistoryTable filter={orderHistoryFilter} />
                    </div>
                  </TabsContent>
                  <TabsContent
                    className="relative min-h-48 flex-1"
                    value="transfers"
                  >
                    <div className="absolute inset-0 h-full w-full pb-12">
                      <TransfersTable />
                    </div>
                  </TabsContent>
                </Tabs>
              </ResizablePanel>
            )}
          </ResizablePanelGroup>
        </div>
        {/* <div className="col-span-4"></div> */}
        <div
          className={cn(
            'flex w-full flex-1 flex-col border-border bg-background transition-opacity md:max-w-md md:flex-none md:border-l',
          )}
        >
          <OrderFormContainer />
          {/* <ConnectionStatus /> */}
        </div>
      </div>
    </div>
  );
}
